import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useCascadingMenu } from './Root'

type UseHandleItemKeyPressArgs = {
  disableSelection?: boolean
  disabledKeys?: string[]
  focusDisabled?: boolean
  hasChildren: boolean
  index: number
  itemDisabled?: boolean
  onDescendMenu: null | (() => void)
  onSelect: null | (() => void)
}

export const useHandleItemKeyPress = <
  TElement extends HTMLElement = HTMLLIElement,
>({
  disabledKeys,
  disableSelection,
  focusDisabled,
  hasChildren,
  index,
  itemDisabled,
  onDescendMenu,
  onSelect,
}: UseHandleItemKeyPressArgs) => {
  const itemRef = useRef<TElement | null>(null)

  const {
    ascendMenuLevel,
    focusedItemIndex,
    interactiveFooter,
    onCloseMenu,
    searchResults,
    setFocusedItemIndex,
    stackLevel,
  } = useCascadingMenu()

  const searchResultCount = useMemo(() => {
    if (!searchResults.length && interactiveFooter) return 1

    return searchResults.length
  }, [searchResults.length, interactiveFooter])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      e.stopPropagation()

      if (disabledKeys?.includes(e.key)) return

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault()
          if (itemDisabled) return
          if (hasChildren && disableSelection) return onDescendMenu?.()
          onSelect?.()
          break
        case 'Escape':
          if (stackLevel > 1) return ascendMenuLevel()
          onCloseMenu?.()
          break
        case 'ArrowDown':
          e.preventDefault()
          if (index === searchResultCount - 1 && !interactiveFooter) return
          setFocusedItemIndex(focusedItemIndex + 1)
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedItemIndex(focusedItemIndex - 1)
          break
        case 'ArrowRight':
          e.preventDefault()
          if (!itemDisabled) onDescendMenu?.()
          break
        case 'ArrowLeft':
          e.preventDefault()
          if (stackLevel > 1) ascendMenuLevel()
          break
        case 'Tab':
          onCloseMenu?.()
          break
      }
    },
    [
      ascendMenuLevel,
      disabledKeys,
      disableSelection,
      focusedItemIndex,
      hasChildren,
      index,
      interactiveFooter,
      itemDisabled,
      onCloseMenu,
      onDescendMenu,
      onSelect,
      searchResultCount,
      setFocusedItemIndex,
      stackLevel,
    ]
  )

  useEffect(() => {
    if (!focusDisabled && focusedItemIndex === index) {
      // Get around other components that may try to steal focus,
      // particularly radix dialog body in modals
      const timer = setTimeout(() => itemRef.current?.focus(), 0)

      return () => clearTimeout(timer)
    }
  }, [focusDisabled, focusedItemIndex, index])

  return { handleKeyPress, itemRef }
}
